"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { env } from "@/env";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { DialogTitle } from "@/components/ui/dialog";
import LoadingSpinner from "@/components/ui/loading-spinner";
import truncate from "@/lib/truncate";
import Image from "next/image";
import { AspectRatio } from "@radix-ui/react-aspect-ratio";
import { useDebounce } from "@/hooks/use-debounce";
import { Badge } from "../ui/badge";
import { Search } from "lucide-react";

type Type = "post" | "game";

interface Suggestion {
  slug: string;
  title: string;
  description?: string;
  tags?: string[];
  thumbnail?: string;
  type: Type;
  genre?: string;
}

export default function MobileSearchBar() {
  const [open, setOpen] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [suggestions, setSuggestions] = React.useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const router = useRouter();

  const debouncedSearchInput = useDebounce(searchInput, 300);

  React.useEffect(() => {
    const fetchData = async () => {
      if (debouncedSearchInput.length < 3) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch(
          `${env.NEXT_PUBLIC_API_URL}/engine/autocomplete?q=${encodeURIComponent(debouncedSearchInput)}`,
        );
        const data = await response.json();
        setSuggestions(data.data?.autocomplete?.result || []);
      } catch (error) {
        console.error("Failed to fetch suggestions:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debouncedSearchInput]);

  const handleSearch = () => {
    const trimmedInput = searchInput.trim();

    if (trimmedInput.length < 3) {
      alert("Search query must be at least 3 characters long.");
      return;
    }

    if (trimmedInput.length > 200) {
      alert("Search query cannot exceed 200 characters.");
      return;
    }

    router.push(
      `/?query=${encodeURIComponent(trimmedInput)}&page=1&sortBy=Upload+Date&orderBy=asc`,
    );
    setOpen(false);
  };

  return (
    <div className="w-auto flex-none">
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 p-0"
        aria-label="Search"
        onClick={() => {
          setOpen((open) => !open);
        }}
      >
        <Search className="h-4 w-4" />
      </Button>
      <CommandDialog shouldFilter={false} open={open} onOpenChange={setOpen}>
        <VisuallyHidden>
          <DialogTitle>Search</DialogTitle>
        </VisuallyHidden>

        <CommandInput
          placeholder="Type a command or search..."
          value={searchInput}
          onValueChange={(value) => {
            setSearchInput(value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
        />
        <CommandList>
          <CommandEmpty className="flex h-32 items-center justify-center">
            {searchInput.length < 3 && !isLoading && (
              <span className="text-sm">
                Type at least 3 characters to search.
              </span>
            )}
            {suggestions.length === 0 &&
              searchInput.length > 2 &&
              !isLoading && <span className="text-sm">No results found.</span>}
            {isLoading && <LoadingSpinner />}
          </CommandEmpty>

          {suggestions.length > 0 && (
            <CommandGroup>
              {suggestions.map((suggestion) => (
                <CommandItem
                  key={suggestion.slug}
                  value={suggestion.title}
                  onSelect={() => {
                    const cleanSlug = suggestion.slug.replace(
                      "suggestion:",
                      "",
                    );
                    if (suggestion.type === "game") {
                      router.push(`/game/${cleanSlug}`);
                    } else {
                      router.push(`/script/${cleanSlug}`);
                    }
                    setOpen(false);
                  }}
                  className="grid grid-cols-5 items-center space-x-2"
                >
                  {suggestion.type === "game" ? (
                    <AspectRatio
                      ratio={16 / 9}
                      className="col-span-1 overflow-hidden"
                    >
                      <Image
                        src={suggestion.thumbnail ?? "/assets/cube.webp"}
                        className="rounded-md"
                        fill
                        alt="Script thumbnail"
                      />
                    </AspectRatio>
                  ) : (
                    <AspectRatio
                      ratio={16 / 9}
                      className="col-span-1 overflow-hidden"
                    >
                      <Image
                        src={`https://cdn.robloxscripts.gg/public/${suggestion.thumbnail}`}
                        className="rounded-md"
                        fill
                        alt="Script thumbnail"
                      />
                    </AspectRatio>
                  )}
                  <div className="col-span-4 flex flex-col items-start">
                    <span className="flex flex-row items-center gap-2">
                      <span className="font-heading font-medium">
                        {suggestion.title}
                      </span>
                      {suggestion.type === "game" && (
                        <Badge variant="accent">Game</Badge>
                      )}
                    </span>
                    <p className="leading-snug text-muted-foreground">
                      {truncate({
                        text: suggestion.description || "",
                        length: 75,
                      })}
                    </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {suggestions.length === 0 && searchInput.length === 0 && (
            <CommandGroup heading="Popular Searches">
              <CommandItem onSelect={() => setSearchInput("Jailbreak")}>
                <span>Jailbreak</span>
              </CommandItem>
              <CommandItem onSelect={() => setSearchInput("Murder Mystery 2")}>
                <span>Murder Mystery 2</span>
              </CommandItem>
              <CommandItem onSelect={() => setSearchInput("Blox Fruits")}>
                <span>Blox Fruits</span>
              </CommandItem>
              <CommandItem onSelect={() => setSearchInput("Da Hood")}>
                <span>Da Hood</span>
              </CommandItem>
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </div>
  );
}
